/**
 * Shared Z Report / Z Report 1 real PDF download (jsPDF).
 * Same Excel-template 2-column design for both reports.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getThemeExportColors } from '../../utils/themeExport';

function cssColorToRgb(value, fallback = [107, 184, 0]) {
  const s = String(value || '').trim();
  if (!s) return fallback;
  if (s.startsWith('#')) {
    let hex = s.slice(1);
    if (hex.length === 3) hex = hex.split('').map((ch) => `${ch}${ch}`).join('');
    if (hex.length >= 6) {
      const r = Number.parseInt(hex.slice(0, 2), 16);
      const g = Number.parseInt(hex.slice(2, 4), 16);
      const b = Number.parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].every((n) => Number.isFinite(n))) return [r, g, b];
    }
  }
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return fallback;
}

function pdfCell(value) {
  if (value === '' || value == null || value === '—' || value === '-') return '-';
  const raw = String(value).replace(/,/g, '').trim();
  if (!raw) return '-';
  const n = Number(raw);
  if (Number.isFinite(n) && n === 0) return '-';
  if (Number.isFinite(n)) {
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return String(value);
}

function stripStrongFlag(row) {
  if (!Array.isArray(row)) return { cells: [], strong: false };
  const strong = row[row.length - 1] === true;
  return { cells: strong ? row.slice(0, -1) : row, strong };
}

function buildTwoColBody(rows) {
  const strongFlags = [];
  const body = (rows || []).map((row) => {
    const { cells, strong } = stripStrongFlag(row);
    strongFlags.push(strong);
    return [String(cells[0] ?? ''), pdfCell(cells[1])];
  });
  return { body, strongFlags };
}

function buildThreeColBody(rows) {
  const strongFlags = [];
  const body = (rows || []).map((row) => {
    const { cells, strong } = stripStrongFlag(row);
    strongFlags.push(strong);
    return [String(cells[0] ?? ''), pdfCell(cells[1]), pdfCell(cells[2])];
  });
  return { body, strongFlags };
}

function buildBankBody(rows) {
  const strongFlags = [];
  const body = (rows || []).map((row) => {
    const { cells, strong } = stripStrongFlag(row);
    strongFlags.push(strong);
    const [label, ...values] = cells;
    return [String(label ?? ''), ...values.map((v) => pdfCell(v))];
  });
  return { body, strongFlags };
}

function drawHeaderBar(doc, {
  x, y, w, h, fill, textColor, text, align = 'center', fontSize = 10,
}) {
  doc.setFillColor(...fill);
  doc.rect(x, y, w, h, 'F');
  doc.setDrawColor(34, 34, 34);
  doc.setLineWidth(0.4);
  doc.rect(x, y, w, h, 'S');
  doc.setTextColor(...textColor);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(fontSize);
  if (align === 'split' && Array.isArray(text)) {
    doc.text(String(text[0] || ''), x + 6, y + h / 2, { baseline: 'middle', align: 'left' });
    doc.text(String(text[1] || ''), x + w - 6, y + h / 2, { baseline: 'middle', align: 'right' });
  } else {
    doc.text(String(text || ''), x + w / 2, y + h / 2, { baseline: 'middle', align: 'center' });
  }
}

function drawSectionPair(doc, {
  startY,
  leftMargin,
  gap,
  leftWidth,
  rightWidth,
  pageWidth,
  leftTitle,
  rightTitle,
  leftHead,
  rightHead,
  leftBody,
  rightBody,
  leftFlags,
  rightFlags,
  primary,
  onPrimary,
  primaryMuted,
  fontSize = 7,
}) {
  const titleH = 14;
  const rightLeft = leftMargin + leftWidth + gap;

  drawHeaderBar(doc, {
    x: leftMargin, y: startY, w: leftWidth, h: titleH,
    fill: primary, textColor: onPrimary, text: leftTitle, fontSize: 8,
  });
  drawHeaderBar(doc, {
    x: rightLeft, y: startY, w: rightWidth, h: titleH,
    fill: primary, textColor: onPrimary, text: rightTitle, fontSize: 8,
  });

  const tableStart = startY + titleH;
  const tableStyles = {
    font: 'helvetica',
    fontStyle: 'bold',
    fontSize,
    cellPadding: 2,
    textColor: [0, 0, 0],
    lineColor: [51, 51, 51],
    lineWidth: 0.35,
    valign: 'middle',
  };
  const headStyles = {
    fillColor: primaryMuted,
    textColor: [0, 0, 0],
    fontStyle: 'bold',
    halign: 'center',
    fontSize,
  };

  autoTable(doc, {
    startY: tableStart,
    head: [leftHead],
    body: leftBody,
    margin: { left: leftMargin, right: pageWidth - leftMargin - leftWidth },
    tableWidth: leftWidth,
    styles: tableStyles,
    headStyles,
    columnStyles: Object.fromEntries(
      leftHead.map((_, i) => [i, { halign: i === 0 ? 'left' : 'right', fontStyle: 'bold' }]),
    ),
    didParseCell: (hookData) => {
      if (hookData.section !== 'body') return;
      if (!leftFlags?.[hookData.row.index]) return;
      hookData.cell.styles.fillColor = [90, 90, 90];
      hookData.cell.styles.textColor = [255, 255, 255];
      hookData.cell.styles.fontStyle = 'bold';
    },
  });
  const leftY = doc.lastAutoTable?.finalY || tableStart;

  autoTable(doc, {
    startY: tableStart,
    head: [rightHead],
    body: rightBody,
    margin: { left: rightLeft, right: pageWidth - rightLeft - rightWidth },
    tableWidth: rightWidth,
    styles: tableStyles,
    headStyles,
    columnStyles: Object.fromEntries(
      rightHead.map((_, i) => [i, { halign: i === 0 ? 'left' : 'right', fontStyle: 'bold' }]),
    ),
    didParseCell: (hookData) => {
      if (hookData.section !== 'body') return;
      if (!rightFlags?.[hookData.row.index]) return;
      hookData.cell.styles.fillColor = [90, 90, 90];
      hookData.cell.styles.textColor = [255, 255, 255];
      hookData.cell.styles.fontStyle = 'bold';
    },
  });
  const rightY = doc.lastAutoTable?.finalY || tableStart;
  return Math.max(leftY, rightY) + 8;
}

/**
 * @param {object} data — paired Z report tables (brandName, bankRows, cashRows, …)
 * @param {string} filename — e.g. z-report-oxy-2026-09-05.pdf
 */
export function downloadPairedZReportPdf(data, filename) {
  const theme = getThemeExportColors();
  const primary = cssColorToRgb(theme.primary, [107, 184, 0]);
  const primaryMuted = cssColorToRgb(theme.primaryMuted, [232, 245, 208]);
  const onPrimary = cssColorToRgb(theme.onPrimary, [15, 18, 24]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 16;
  const contentW = pageWidth - leftMargin * 2;
  const gap = 8;
  const leftWidth = Math.floor(contentW * 0.58);
  const rightWidth = contentW - leftWidth - gap;
  let y = 14;

  const barH = 16;
  drawHeaderBar(doc, {
    x: leftMargin, y, w: contentW, h: barH,
    fill: primary, textColor: onPrimary, text: data.brandName, fontSize: 11,
  });
  y += barH;
  if (data.branch) {
    drawHeaderBar(doc, {
      x: leftMargin, y, w: contentW, h: 14,
      fill: primary, textColor: onPrimary, text: data.branch, fontSize: 9,
    });
    y += 14;
  }
  drawHeaderBar(doc, {
    x: leftMargin, y, w: contentW, h: 15,
    fill: primary, textColor: onPrimary,
    text: ['Z-REPORT', `DATE : ${data.dayLabel}`],
    align: 'split',
    fontSize: 10,
  });
  y += 20;

  const bankAccounts = data.bankAccounts || [];
  const bank = buildBankBody(data.bankRows);
  const cash = buildTwoColBody(data.cashRows);
  y = drawSectionPair(doc, {
    startY: y,
    leftMargin,
    gap,
    leftWidth,
    rightWidth,
    pageWidth,
    leftTitle: 'Bank Summery',
    rightTitle: 'Cash Summery',
    leftHead: ['Item', ...bankAccounts],
    rightHead: ['Item', 'Amount'],
    leftBody: bank.body,
    rightBody: cash.body,
    leftFlags: bank.strongFlags,
    rightFlags: cash.strongFlags,
    primary,
    onPrimary,
    primaryMuted,
    fontSize: 6.5,
  });

  const todaySale = buildThreeColBody(data.saleSummary);
  const monthSale = buildTwoColBody(data.saleSummaryManual);
  y = drawSectionPair(doc, {
    startY: y,
    leftMargin,
    gap,
    leftWidth,
    rightWidth,
    pageWidth,
    leftTitle: 'Today Sale Summery',
    rightTitle: 'During Month Sale Summery',
    leftHead: ['Item', 'No', 'Amount'],
    rightHead: ['Item', 'Amount'],
    leftBody: todaySale.body,
    rightBody: monthSale.body,
    leftFlags: todaySale.strongFlags,
    rightFlags: monthSale.strongFlags,
    primary,
    onPrimary,
    primaryMuted,
    fontSize: 6.5,
  });

  const todayPay = buildThreeColBody(data.paymentMethods);
  const monthPay = buildTwoColBody(data.saleMethods);
  y = drawSectionPair(doc, {
    startY: y,
    leftMargin,
    gap,
    leftWidth,
    rightWidth,
    pageWidth,
    leftTitle: 'Today Payment Method',
    rightTitle: 'During Month Payment Method',
    leftHead: ['Item', 'No', 'Amount'],
    rightHead: ['Item', 'Amount'],
    leftBody: todayPay.body,
    rightBody: monthPay.body,
    leftFlags: todayPay.strongFlags,
    rightFlags: monthPay.strongFlags,
    primary,
    onPrimary,
    primaryMuted,
    fontSize: 7,
  });

  const active = buildTwoColBody(data.activeMembers);
  const expense = buildTwoColBody(data.expenseSummary);
  drawSectionPair(doc, {
    startY: y,
    leftMargin,
    gap,
    leftWidth,
    rightWidth,
    pageWidth,
    leftTitle: 'Total Active Members',
    rightTitle: 'Total Expense Summery',
    leftHead: ['Item', 'Amount'],
    rightHead: ['Item', 'Amount'],
    leftBody: active.body,
    rightBody: expense.body,
    leftFlags: active.strongFlags,
    rightFlags: expense.strongFlags,
    primary,
    onPrimary,
    primaryMuted,
    fontSize: 7,
  });

  const name = String(filename || 'z-report.pdf');
  doc.save(name.endsWith('.pdf') ? name : `${name}.pdf`);
  return true;
}
